import { GatsbyImage, getImage } from "gatsby-plugin-image";
import React, {useState, useEffect, Suspense} from "react";
import { PrismicRichText } from "@prismicio/react";
import { Link } from "gatsby";
import VideoModal from "../videoModal";
import { components } from "../../utils/rt-mods";
import { colorCheck } from "../../utils/accessibility-helpers";
import ScriptInjector from "../script-injector";
// Dynamic import for Lottie to avoid SSR issues
const Lottie = React.lazy(() => import("lottie-react"));
import Bowser from "bowser";

export const SideBySide = (props) => {
  const sc = props.slice.primary;

  const button = props.slice.items;

  let space = sc.spacing;

  if (space === "Both large") {
    space = "gutter";
  } else if (space === "Both small") {
    space = "gutter sm";
  } else if (space === "Top large") {
    space = "gutter bot";
  } else if (space === "Top small") {
    space = "gutter bot sm";
  } else if (space === "Bottom large") {
    space = "gutter top";
  } else {
    space = "gutter top sm";
  }

  let bg =
    sc.background === "gradient"
      ? "gradient-to-b from-grey-light to-white"
      : sc.background === "grey"
      ? "grey-light"
      : sc.background === "dark grey"
      ? "grey-d"
      : sc.background;

  let layout = sc.layout === "Left" ? "md:flex-row" : "md:flex-row-reverse";

  const anchor = sc.title.text.replace(/\s+/g, "-").toLowerCase();

  const lotty = JSON.parse(sc.lottie_json);

  let bgImg = sc.background_image != null ? {backgroundImage: `url(${sc.background_image.url})`} : ''
  
  const interactivity = {
    mode: "scroll",
    actions: [
      {
        visibility: [0, 1],
        type: "play",
      },
    ],
  };

  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const isSafariBrowser = browser.getBrowserName() === "Safari";
    setIsSafari(isSafariBrowser);
  }, []);

  console.log(isSafari)

  return (
    <section
      id={anchor}
      className={"content-side-side bg-" + bg + " " + sc.theme + " " + space}
    >
      {bgImg != null ? <div style={bgImg} className={"section-bg" + (sc.fade ? ' fade' : '')} /> : ''}
      <div
        className={
          "wrapper flex flex-col justify-between gap-6 md:gap-24 " +
          layout +
          " " +
          (sc.alignment ? "lg:items-" + sc.alignment : "lg:items-center")
        }
      >
        <div className={"w-full md:w-1/2 content-side text-" + (sc.content_color || 'grey')}>
          {sc.label ? (
            <div
              className={
                "label bg-opacity-10 font-bold uppercase tracking-widest text-xs mb-1 md:mb-2 text-" +
                colorCheck(sc.theme)
              }
            >
              {sc.label}
            </div>
          ) : (
            ""
          )}
          <PrismicRichText field={sc.title.richText} />
          <PrismicRichText
            field={sc.content.richText}
            components={components}
          />
          {sc.custom_code ? <ScriptInjector html={sc.custom_code} /> : "" }
          <div className="flex items-center flex-wrap gap-2">
            {button.map((btn, index) => {
              if (btn.button_link == null) {
                return "";
              }
              return (
                <Link
                  key={index}
                  to={btn.button_link.url}
                  target={
                    btn.button_link.target === "_blank"
                      ? btn.button_link.target
                      : "_self"
                  }
                >
                  <div className={"btn bg-" + colorCheck(btn.button_color)}>
                    {btn.button_label}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        {sc.support === "Media" ? (
          <div className={"relative w-full md:w-1/2 " + (sc.shadow ? "rounded-3xl shadow-xl" : "") +  " " + (sc.css_classes ? sc.css_classes : "")}>
            {sc.video ? (
              <VideoModal src={sc.video} title={sc.title.text} />
            ) : (
              ""
            )}
            <GatsbyImage
              image={getImage(sc.image)}
              className={(sc.shadow ? "rounded-3xl" : "") + (sc.video ? " !hidden lg:!block" : "")}
              alt={sc.image.alt}
            />
            {sc.video ? (
              <div className="video-wrapper block lg:hidden">
                <iframe
                  src={"https://player.vimeo.com/video/" + sc.video}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  title={sc.title.text}
                />
              </div>
            ) : (
              ""
            )}
          </div>
        ) : (
          ""
        )}
        {sc.support === "Quote" ? (
          <div className="relative w-full bg-grey-light p-12 md:w-1/2 rounded-2xl">
            <div className={"mb-6 text-" + sc.theme}>
              <i className="fa-solid fa-quote-left text-4xl md:text-6xl"></i>
            </div>
            <div>
              <PrismicRichText field={sc.quote_content.richText} />
            </div>
            <div>
              <strong className="mt-6 block">{sc.quote_author}</strong>
              <span className="block text-sm">{sc.quote_author_title}</span>
            </div>
          </div>
        ) : (
          ""
        )}
        {sc.support === "Resource" ? (
          <div className="w-full md:w-2/4 css">
            <Link
              to={sc.card_resource.url}
              className="card w-full md:w-3/4 m-auto"
            >
              <span>
                <span className="relative block">
                  <GatsbyImage
                    image={getImage(sc.card_resource.document.data.meta_image)}
                    alt={
                      sc.card_resource.document.data.meta_image.alt ||
                      sc.card_resource.document.data.resource_title.text
                    }
                    className="rounded-t-lg"
                  />
                  <div className="bottom-7 left-7 absolute bg-black bg-opacity-60 inline-block pr-1 pl-2 pt-1 pb-1 rounded font-bold uppercase tracking-widest text-vs text-white">
                    {sc.card_resource.document.data.resource_label}
                  </div>
                </span>
                <span className="p-7 block">
                  <span className="title text-xl font-bold mb-3 pr-6 block leading-tight">
                    <PrismicRichText
                      field={
                        sc.card_resource.document.data.resource_title.richText
                      }
                    />
                  </span>
                  <p className="mb-0">
                    {sc.card_resource.document.data.description.text}
                  </p>
                </span>
              </span>
              <span className="pb-7 pr-7 pl-7 block">
                <div className="btn bg-yellow">
                  {sc.card_resource_button_label
                    ? sc.card_resource_button_label
                    : "Learn more"}
                </div>
              </span>
            </Link>
          </div>
        ) : (
          ""
        )}
        {sc.support === "Custom" ? (
          <div className="w-full md:w-2/4 css">
            <ScriptInjector html={sc.custom_html} />
          </div>
        ) : (
          ""
        )}
        {sc.support === "Animation" ? 
        (sc.lottie_json === null ?
          <div className="w-full md:w-2/4 v">
            <video className="min-h-[600px] hidden md:block" autoPlay muted preload="auto">
            <source src={sc.video_animation_hvec.url} type='video/mp4;codecs=hvc1' />
            <source src={sc.video_animation.url} type="video/webm" />
            </video>
            <div className="block md:hidden">
              <GatsbyImage
                image={getImage(sc.image)}
                className={
                  (sc.shadow ? "rounded-3xl" : "") +
                  (sc.video ? " !hidden lg:!block" : "")
                }
                alt={sc.image.alt}
              />
            </div>
          </div> :
          <div className="w-full md:w-2/4 l">
            {typeof window !== 'undefined' ? (
              <Suspense fallback={<div>Loading animation...</div>}>
                <Lottie animationData={lotty} interactivity={interactivity} aria-aria-labelledby="use lottie animation" loop={false} autoPlay={false} />
              </Suspense>
            ) : (
              <div>Loading animation...</div>
            )}
          </div>
          )
         : 
          ""
        }
      </div>
    </section>
  );
};
